<template>
  <div class="panel panel-default">
    <div class="panel-heading">
      <div class="row">
        <div class="col-xs-9">
          <h5>
            Reminders
          </h5>
        </div>

        <div v-if="!loading" style="margin-top: 4px">
          <div class="col-xs-5">
            <SelectWidget
                v-model="status"
                class="form-control input-sm"
                :nullable="true"
                placeholder="(all)"
                :options="[{value: 'resolved', label: 'Resolved'}, {value: 'unresolved', label: 'Unresolved'}]"
            />
          </div>

          <div class="col-xs-5">
            <SelectWidget
                v-model="portal"
                class="form-control input-sm"
                :nullable="true"
                placeholder="(all portals)"
                :options="portals.map(portal => ({value: portal.id, label: portal.attributes.name}))"
            />
          </div>

          <div class="col-xs-5">
            <button
                type="button"
                class="btn btn-black btn-block btn-sm"
                data-toggle="modal"
                data-target="#create-reminder-modal[data-name=reminder-list]"
            >
              <i class="fa fa-plus"></i> Add
            </button>
          </div>
        </div>
      </div>
    </div>

    <loading-spinner v-if="loading" :style="loadingSpinnerStyle"></loading-spinner>

    <ul v-else class="list-group">
      <li v-for="reminder in reminders" class="list-group-item" style="padding: 10px 0">
        <div style="display: flex; flex-direction: row">
          <div class="text-muted"
               style="flex: 1; font-size: 24px; display: flex; justify-content: center; align-items: center;">
            <i class="fa fa-bell"></i>
          </div>

          <div style="flex: 7 1 0%;">
            <div>
              <b>
                {{ reminder.attributes.user_full_name }}
              </b>

              <span v-if="reminder.attributes.priority === 'high'" class="label label-danger">
                High
              </span>

              <span v-if="reminder.attributes.priority === 'medium'" class="label label-warning">
                Medium
              </span>

              <span v-if="reminder.attributes.priority === 'low'" class="label label-success">
                Low
              </span>
            </div>

            <div v-if="reminder.attributes.portals.length">
              <i>
                {{ reminder.attributes.portals.map(portal => portal.name).join(' &middot; ') }}<span v-if="reminder.attributes.project_id"> | {{ reminder.attributes.project_name }}</span>
              </i>
            </div>

            <span>
              {{ reminder.attributes.text }}
            </span>

            <br>

            <i class="text-muted" :style="{color: getDiff(reminder) < 0 ? '#d9534f' : 'inherit'}">
              {{ getDeadlineDescription(reminder) }}
            </i>
          </div>

          <div style="flex: 1; font-size: 24px; display: flex; justify-content: flex-end; align-items: center;">
            <button type="button" class="btn btn-lg" @click="edit(reminder)" style="background-color: #ffffff">
              <i class="fa fa-edit"></i>
            </button>

            <button type="button" class="btn btn-lg" @click="postpone(reminder)" style="background-color: #ffffff">
              <i class="fa fa-clock"></i>
            </button>

            <button type="button" class="btn btn-lg" @click="resolve(reminder)" style="background-color: #ffffff">
              <i class="fa fa-check-circle"></i>
            </button>
          </div>
        </div>
      </li>

      <li v-if="reminders.length === 0" class="list-group-item text-center" style="padding: 50px;">
        There are no reminders to display.
      </li>
    </ul>

    <CreateReminderModal :subject="{data: company}" @created="load" data-name="reminder-list"/>

    <PostponeReminderModal :reminder="editing" @postponed="load" data-name="reminder-list"/>

    <UpdateReminderModal :reminder="editing" @updated="load" data-name="reminder-list"/>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import notify from '@/notify'
import moment from 'moment'
import CreateReminderModal from '@/pages/Shared/Modals/CreateReminder'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import PostponeReminderModal from '@/components/Modals/Reminders/PostponeReminderModal'
import SaveHeight from '@/mixins/SaveHeight'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import TaskListIcon from '@/pages/Dashboards/MyFocus/Panels/TaskList/TaskListIcon'
import UpdateReminderModal from '@/pages/Shared/Modals/UpdateReminder'

export default {
  components: {
    CreateReminderModal,
    LoadingSpinner,
    PostponeReminderModal,
    SelectWidget,
    TaskListIcon,
    UpdateReminderModal
  },

  mixins: [SaveHeight],

  props: {
    company: {
      required: true
    }
  },

  data() {
    return {
      reminders: null,
      portals: null,

      status: 'unresolved',
      portal: null,

      editing: null,

      loading: true,
    }
  },

  mounted() {
    this.load()
  },

  watch: {
    status() {
      this.loadReminders()
    },

    portal() {
      this.loadReminders()
    }
  },

  methods: {
    load() {
      Promise.all([
        this.getReminderPromise(),
        http.get('/api/v2/portals')
      ]).then(responses => {
        this.reminders = responses[0].data.data
        this.portals = responses[1].data.data

        this.loading = false
      })
    },

    loadReminders() {
      this.saveHeight()

      this.loading = true

      this.getReminderPromise().then(response => {
        this.reminders = response.data.data

        this.loading = false
      })
    },

    getReminderPromise() {
      return http.get('/api/v2/reminders', {
        'filter[status]': this.status,
        'filter[portal_id]': this.portal,
        'filter[company_id]': this.company.id,
      })
    },

    getDeadlineDescription(reminder) {
      let diff = this.getDiff(reminder)

      if (diff === 0) {
        return 'Today at ' + moment(reminder.attributes.remind_at).format('HH:mm')
      }

      if (diff === 1) {
        return 'Tomorrow at ' + moment(reminder.attributes.remind_at).format('HH:mm')
      }

      if (diff === -1) {
        return 'Yesterday at ' + moment(reminder.attributes.remind_at).format('HH:mm')
      }

      if (diff > 1) {
        return 'In ' + diff + ' days'
      }

      if (diff < 1) {
        return Math.abs(diff) + ' days ago'
      }
    },

    saveHeight() {
      if (this.$el.querySelector('.list-group')) {
        this.height = this.$el.querySelector('.list-group').clientHeight
      }
    },

    getDiff(reminder) {
      return moment(reminder.attributes.remind_at).diff(moment().startOf('day'), 'days')
    },

    resolve(reminder) {
      modal.confirm('Are you sure that you want to resolve the reminder?', ()=> {
        http.post('/api/v2/reminders/' + reminder.id + '/resolve')
            .then(() => {
              notify.success('The reminder was resolved')

              this.load()
            })
      })
    },

    postpone(reminder) {
      this.editing = reminder

      $('#postpone-reminder-modal[data-name=reminder-list]').modal('show')
    },

    edit(logLine) {
      this.editing = logLine

      $('#update-reminder-modal[data-name=reminder-list]').modal('show')
    }
  }
}
</script>
